import type { ChannelModelRecord } from '@nadoutong/contracts';
import { useUpdateChannelModel } from '../../api/useChannelModels.ts';
import { useSkuReadiness } from '../../api/skuReadiness.ts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Switch } from '../../ui/index.ts';
import { useToast, coerceErrorMessage } from '../Toast.tsx';
import { useMemo } from 'react';

/**
 * The public-shelf switch (#1407).
 *
 * `org_visibility_default` is the bottom rung of the visibility ladder:
 * `COALESCE(per-org override, per-namespace override, this, 0)`. Since #1156
 * every new SKU is born off the shelf and — until this change — the column had
 * no write path at all, so listing a SKU for everyone meant one grant per
 * organization, forever. The owner's Option A call restores the lever with the
 * gate that keeps #1156's intent: a SKU cannot go on the shelf unpriced or
 * unroutable.
 *
 * ── Why the control mirrors the server gate rather than trusting it ────────
 *
 * The server refuses a premature flip on its own — this switch could be a bare
 * toggle that surfaces the error. It is gated client-side too because the
 * operator needs to know WHY before they click, and because the disabled
 * reason and the server's refusal are then two readings of one pair of facts:
 *
 *  - `priced` comes from the catalog record, and since #1407 that field is the
 *    platform's canonical "a `model_pricing` row is in force" — the very
 *    predicate the gate applies. It is no longer `rate_multiplier > 0`.
 *  - the three routing bits come from `useSkuReadiness`, which is the same
 *    gateway endpoint the gate calls server-side and the same one the
 *    readiness checklist renders.
 *
 * Taking a SKU off the shelf is never gated, on either side: it can only
 * narrow reach, and a SKU that has just become unsellable is exactly the one
 * that most needs to come down.
 */
export function ShelfToggle({ record }: { readonly record: ChannelModelRecord }): JSX.Element {
  const t = useT();
  const toast = useToast();
  const updateMut = useUpdateChannelModel();
  const skuIds = useMemo(() => [record.sku_id], [record.sku_id]);
  const readiness = useSkuReadiness(skuIds);
  const bits = readiness.bitsBySku.get(record.sku_id);

  const on = record.org_visibility_default;

  // Why the flip to visible is unavailable, or null when it is available.
  // Order matches the checklist's own ("price it, then give it supply") and
  // the server's, so the reason shown is the row an operator would fix first.
  const blockedKey: I18nKey | null = (() => {
    if (on) return null;
    if (!record.priced) return 'sku_detail.shelf.blocked_pricing';
    if (readiness.isPending) return 'sku_detail.shelf.blocked_checking';
    if (readiness.isError || bits === undefined) return 'sku_detail.shelf.blocked_unknown';
    if (!bits.binding) return 'sku_detail.shelf.blocked_binding';
    if (!bits.probe) return 'sku_detail.shelf.blocked_probe';
    if (!bits.cost) return 'sku_detail.shelf.blocked_cost';
    return null;
  })();

  function handleChange(next: boolean): void {
    updateMut.mutate(
      { skuId: record.sku_id, body: { org_visibility_default: next } },
      {
        onSuccess: () => toast.success(
          t(next ? 'sku_detail.shelf.toast_listed' : 'sku_detail.shelf.toast_delisted'),
        ),
        onError: (error) => toast.error(coerceErrorMessage(error)),
      },
    );
  }

  return (
    <div className="flex flex-col gap-2 border-t border-line-soft pt-4" data-testid="sku-shelf-control">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-body text-paper">{t('sku_detail.shelf.label')}</span>
          <span className="text-label text-paper-low">
            {t(on ? 'sku_detail.shelf.state_listed' : 'sku_detail.shelf.state_private')}
          </span>
        </div>
        <Switch
          checked={on}
          onCheckedChange={handleChange}
          disabled={updateMut.isPending || blockedKey !== null}
          testId="sku-shelf-toggle"
        />
      </div>
      {blockedKey === null ? null : (
        <p className="m-0 text-label text-warn" data-testid="sku-shelf-blocked">
          {t(blockedKey)}
        </p>
      )}
    </div>
  );
}
